/**
 * Estado vacío del mapa — la pantalla más importante de la Era Pionera.
 *
 * Regla de Marcus R. que gobierna todo el componente:
 *
 *   El vacío se enuncia como DECISIÓN del producto, nunca como resultado.
 *   "No hay nada" es un fallo; "abrimos en cero a propósito" es una
 *   invitación.
 *
 * Prohibido aquí: --gu-danger, iconos de alerta, spinners, la palabra
 * "resultados", ilustraciones de caja vacía y cualquier botón de reintentar.
 * Nada de esto es un error.
 */

export interface EmptyMapProps {
  /** Lugares que existen en toda la ciudad (no solo en el viewport). */
  cityPlaceCount: number;
  onAddPlace: () => void;
  onHowItWorks?: (() => void) | undefined;
  /** Solo se pasa en la variante local, para orientar al usuario. */
  onSeeWhereThereAre?: (() => void) | undefined;
}

export function EmptyMap({
  cityPlaceCount,
  onAddPlace,
  onHowItWorks,
  onSeeWhereThereAre,
}: EmptyMapProps) {
  // Variante A (ciudad en cero) vs B (hay lugares, pero no por aquí).
  // Mostrar A cuando ya existen lugares sería mentir sobre el estado real.
  const ciudadEnCero = cityPlaceCount === 0;

  return (
    <section
      aria-labelledby="empty-title"
      style={{
        position: 'absolute',
        insetInline: 0,
        bottom: 0,
        background: 'var(--gu-surface-raised)',
        borderRadius: 'var(--gu-radius-lg) var(--gu-radius-lg) 0 0',
        boxShadow: 'var(--gu-shadow-md)',
        padding: 'var(--gu-space-6) var(--gu-space-5) var(--gu-space-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gu-space-4)',
        zIndex: 'var(--gu-z-sheet)' as unknown as number,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: '2rem',
          lineHeight: 1,
          color: 'var(--gu-text-tertiary)',
        }}
      >
        ◇
      </span>

      {ciudadEnCero ? (
        <>
          <h2
            id="empty-title"
            style={{
              fontSize: 'var(--gu-text-h1)',
              lineHeight: 'var(--gu-leading-tight)',
              margin: 0,
            }}
          >
            Este mapa está en blanco.
            <br />
            Es lo que queríamos.
          </h2>
          <p
            style={{
              margin: 0,
              color: 'var(--gu-text-secondary)',
              lineHeight: 'var(--gu-leading-relaxed)',
            }}
          >
            Gourmet Urbano abre en cero. No hay lugares de relleno, ni listas
            copiadas de internet. Los primeros los pones tú.
          </p>
        </>
      ) : (
        <>
          <h2
            id="empty-title"
            style={{
              fontSize: 'var(--gu-text-h1)',
              lineHeight: 'var(--gu-leading-tight)',
              margin: 0,
            }}
          >
            Por aquí todavía no hay nadie.
          </h2>
          <p
            style={{
              margin: 0,
              color: 'var(--gu-text-secondary)',
              lineHeight: 'var(--gu-leading-relaxed)',
            }}
          >
            {cityPlaceCount === 1
              ? 'Hay 1 lugar en la ciudad, ninguno en esta zona.'
              : `Hay ${cityPlaceCount} lugares en la ciudad, ninguno en esta zona.`}{' '}
            Puedes ser el primero.
          </p>
        </>
      )}

      <button
        type="button"
        onClick={onAddPlace}
        style={{
          minHeight: 'var(--gu-touch-comfortable)',
          padding: '0 var(--gu-space-5)',
          borderRadius: 'var(--gu-radius-md)',
          // --gu-primary-strong: blanco encima solo pasa AA con esta
          // variante (6.14:1 vs 4.28:1). Ver docs/06 §auditoría.
          background: 'var(--gu-primary-strong)',
          color: 'var(--gu-white)',
          border: 'none',
          fontSize: 'var(--gu-text-body)',
          fontWeight: 550,
          cursor: 'pointer',
        }}
      >
        {ciudadEnCero ? 'Marcar el primer lugar' : 'Marcar un lugar aquí'}
      </button>

      {ciudadEnCero && onHowItWorks && (
        <TextLink onClick={onHowItWorks}>¿Cómo funciona esto?</TextLink>
      )}
      {!ciudadEnCero && onSeeWhereThereAre && (
        <TextLink onClick={onSeeWhereThereAre}>Ver dónde sí hay</TextLink>
      )}
    </section>
  );
}

function TextLink({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        // --gu-text-link, no --gu-primary: este último falla contraste
        // como color de texto (4.01:1).
        color: 'var(--gu-text-link)',
        fontSize: 'var(--gu-text-body)',
        minHeight: 'var(--gu-touch-min)',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}
